import axios from 'axios';
import authService from './authService.js';

// Configuración de FileMaker para el sistema yooladmin
const FILEMAKER_CONFIG = {
  apiUrl: process.env.REACT_APP_FILEMAKER_API_URL,
  database: process.env.REACT_APP_FILEMAKER_DATABASE,
  token: process.env.REACT_APP_FILEMAKER_TOKEN
};

/**
 * Servicio de configuración del sistema
 * Gestiona la configuración global desde FileMaker
 */
class ConfigService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.defaultCacheTime = 600000; // 10 minutos
  }

  /**
   * Obtiene todas las configuraciones del sistema
   */
  async getAllConfigs() {
    const cacheKey = 'all_configs';
    
    if (this.isValidCache(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const response = await axios.get(
        `${FILEMAKER_CONFIG.apiUrl}/fmi/data/vLatest/databases/${FILEMAKER_CONFIG.database}/layouts/system_config_yoolusers/records`,
        {
          headers: {
            'Authorization': `Bearer ${FILEMAKER_CONFIG.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const configs = {};
      response.data.response.data.forEach(record => {
        const fieldData = record.fieldData;
        configs[fieldData.config_key] = {
          value: this.parseConfigValue(fieldData.config_value, fieldData.value_type),
          rawValue: fieldData.config_value,
          type: fieldData.value_type,
          description: fieldData.description,
          category: fieldData.category,
          isActive: fieldData.is_active,
          lastModified: fieldData.last_modified
        };
      });

      this.setCache(cacheKey, configs);
      return configs;
    } catch (error) {
      console.error('Error fetching system configs:', error);
      return this.getDefaultConfigs();
    }
  }

  /**
   * Obtiene una configuración específica por clave
   */
  async getConfig(key) {
    const cacheKey = `config_${key}`;
    
    if (this.isValidCache(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const allConfigs = await this.getAllConfigs();
      const config = allConfigs[key];
      
      if (config) {
        this.setCache(cacheKey, config);
        return config;
      }
      
      return this.getDefaultConfig(key);
    } catch (error) {
      console.error(`Error fetching config ${key}:`, error);
      return this.getDefaultConfig(key);
    }
  }

  /**
   * Obtiene el valor de una configuración (solo el valor, no el objeto completo)
   */
  async getConfigValue(key) {
    const config = await this.getConfig(key);
    return config ? config.value : null;
  }

  /**
   * Actualiza una configuración en FileMaker
   */
  async updateConfig(key, value, type = 'string') {
    try {
      // Primero obtener el record ID
      const response = await axios.get(
        `https://${FILEMAKER_CONFIG.host}/fmi/data/vLatest/databases/${FILEMAKER_CONFIG.database}/layouts/${FILEMAKER_CONFIG.layout}/records`,
        {
          headers: {
            'Authorization': `Bearer ${FILEMAKER_CONFIG.apiKey}`,
            'Content-Type': 'application/json'
          },
          params: {
            '_find.config_key': key
          }
        }
      );

      if (response.data.response.data.length === 0) {
        throw new Error(`Configuration key '${key}' not found`);
      }

      const recordId = response.data.response.data[0].recordId;
      
      // Actualizar el registro
      const updateResponse = await axios.patch(
        `https://${FILEMAKER_CONFIG.host}/fmi/data/vLatest/databases/${FILEMAKER_CONFIG.database}/layouts/${FILEMAKER_CONFIG.layout}/records/${recordId}`,
        {
          fieldData: {
            config_value: value.toString(),
            value_type: type,
            last_modified: new Date().toISOString()
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${FILEMAKER_CONFIG.apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Limpiar caché relacionado
      this.clearCache(`config_${key}`);
      this.clearCache('all_configs');

      return updateResponse.data;
    } catch (error) {
      console.error(`Error updating config ${key}:`, error);
      throw error;
    }
  }

  /**
   * Crea una nueva configuración
   */
  async createConfig(key, value, type = 'string', description = '', category = 'general') {
    try {
      const response = await axios.post(
        `https://${FILEMAKER_CONFIG.host}/fmi/data/vLatest/databases/${FILEMAKER_CONFIG.database}/layouts/${FILEMAKER_CONFIG.layout}/records`,
        {
          fieldData: {
            config_key: key,
            config_value: value.toString(),
            value_type: type,
            description: description,
            category: category,
            is_active: true,
            created_at: new Date().toISOString(),
            last_modified: new Date().toISOString()
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${FILEMAKER_CONFIG.apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Limpiar caché
      this.clearCache('all_configs');
      
      return response.data;
    } catch (error) {
      console.error(`Error creating config ${key}:`, error);
      throw error;
    }
  }

  /**
   * Obtiene configuraciones por categoría
   */
  async getConfigsByCategory(category) {
    const allConfigs = await this.getAllConfigs();
    const categoryConfigs = {};
    
    Object.keys(allConfigs).forEach(key => {
      if (allConfigs[key].category === category) {
        categoryConfigs[key] = allConfigs[key];
      }
    });
    
    return categoryConfigs;
  }

  /**
   * Obtiene configuraciones de pagos
   */
  async getPaymentConfigs() {
    return await this.getConfigsByCategory('payment');
  }

  /**
   * Obtiene configuraciones de sistema
   */
  async getSystemConfigs() {
    return await this.getConfigsByCategory('system');
  }

  /**
   * Obtiene configuraciones de seguridad
   */
  async getSecurityConfigs() {
    return await this.getConfigsByCategory('security');
  }

  /**
   * Verifica si el sistema está en modo mantenimiento
   */
  async isMaintenanceMode() {
    const config = await this.getConfigValue('maintenance_mode');
    return config === true || config === 'true';
  }

  /**
   * Obtiene el número máximo de intentos de pago
   */
  async getMaxPaymentRetries() {
    const config = await this.getConfigValue('payment_retry_max_attempts');
    return parseInt(config) || 3;
  }

  /**
   * Obtiene el período de gracia de suscripción en días
   */
  async getSubscriptionGracePeriod() {
    const config = await this.getConfigValue('subscription_grace_period_days');
    return parseInt(config) || 7;
  }

  /**
   * Parsea el valor de configuración según su tipo
   */
  parseConfigValue(value, type) {
    switch (type) {
      case 'boolean':
        return value === 'true' || value === true;
      case 'integer':
        return parseInt(value) || 0;
      case 'float':
        return parseFloat(value) || 0.0;
      case 'json':
        try {
          return JSON.parse(value);
        } catch {
          return {};
        }
      default:
        return value;
    }
  }

  /**
   * Obtiene configuraciones por defecto
   */
  getDefaultConfigs() {
    return {
      payment_retry_max_attempts: {
        value: 3,
        type: 'integer',
        description: 'Número máximo de intentos de pago',
        category: 'payment'
      },
      subscription_grace_period_days: {
        value: 7,
        type: 'integer',
        description: 'Días de gracia para suscripciones',
        category: 'payment'
      },
      maintenance_mode: {
        value: false,
        type: 'boolean',
        description: 'Modo de mantenimiento del sistema',
        category: 'system'
      },
      max_concurrent_sessions: {
        value: 5,
        type: 'integer',
        description: 'Máximo de sesiones concurrentes por usuario',
        category: 'security'
      },
      session_timeout_minutes: {
        value: 30,
        type: 'integer',
        description: 'Tiempo de expiración de sesión en minutos',
        category: 'security'
      },
      auto_logout_enabled: {
        value: true,
        type: 'boolean',
        description: 'Habilitar logout automático',
        category: 'security'
      },
      debug_mode: {
        value: false,
        type: 'boolean',
        description: 'Modo de depuración',
        category: 'system'
      }
    };
  }

  /**
   * Obtiene una configuración por defecto específica
   */
  getDefaultConfig(key) {
    const defaults = this.getDefaultConfigs();
    return defaults[key] || null;
  }

  /**
   * Verifica si el caché es válido
   */
  isValidCache(key) {
    const expiry = this.cacheExpiry.get(key);
    return expiry && Date.now() < expiry && this.cache.has(key);
  }

  /**
   * Establece un valor en caché
   */
  setCache(key, value, ttl = this.defaultCacheTime) {
    this.cache.set(key, value);
    this.cacheExpiry.set(key, Date.now() + ttl);
  }

  /**
   * Limpia un elemento específico del caché
   */
  clearCache(key) {
    this.cache.delete(key);
    this.cacheExpiry.delete(key);
  }

  /**
   * Limpia todo el caché
   */
  clearAllCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Refresca todas las configuraciones desde FileMaker
   */
  async refreshConfigs() {
    this.clearAllCache();
    return await this.getAllConfigs();
  }
}

// Instancia singleton
const configService = new ConfigService();

// Funciones de utilidad para usar en componentes
export const useConfig = () => {
  return {
    getConfig: (key) => configService.getConfig(key),
    getConfigValue: (key) => configService.getConfigValue(key),
    getAllConfigs: () => configService.getAllConfigs(),
    updateConfig: (key, value, type) => configService.updateConfig(key, value, type),
    isMaintenanceMode: () => configService.isMaintenanceMode(),
    getPaymentConfigs: () => configService.getPaymentConfigs(),
    refreshConfigs: () => configService.refreshConfigs()
  };
};

// Exportaciones específicas
export const getConfig = (key) => configService.getConfig(key);
export const getConfigValue = (key) => configService.getConfigValue(key);
export const getAllConfigs = () => configService.getAllConfigs();
export const updateConfig = (key, value, type) => configService.updateConfig(key, value, type);
export const createConfig = (key, value, type, description, category) => 
  configService.createConfig(key, value, type, description, category);
export const isMaintenanceMode = () => configService.isMaintenanceMode();
export const getPaymentConfigs = () => configService.getPaymentConfigs();
export const getSystemConfigs = () => configService.getSystemConfigs();
export const getSecurityConfigs = () => configService.getSecurityConfigs();
export const refreshConfigs = () => configService.refreshConfigs();

export default configService;
